/**
 * Messages metrics tab -- total messages, human/agent ratio, recall rate.
 * DatePresets + KPI cards + TrendLineChart (3 lines) + BarDistribution + collapsible tables.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { MessageAnalytics } from "../../api/admin-metrics";
import { fetchMessageAnalytics } from "../../api/admin-metrics";
import { Td, Th, tableStyles } from "../../components/DataTable";
import { DatePresets } from "../../components/DatePresets";
import { BarDistribution } from "../../components/charts/BarDistribution";
import { TrendLineChart } from "../../components/charts/TrendLineChart";
import { t } from "../../i18n";
import * as shared from "../../styles/shared";
import { fmt, pct } from "./helpers";

function defaultRange(): { start_date: string; end_date: string } {
  const end = new Date().toISOString().slice(0, 10);
  const start = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  return { start_date: start, end_date: end };
}

export function MessagesTab(): React.JSX.Element {
  const [range, setRange] = useState(defaultRange);
  const { data, error, loading, load } = useMessageData();
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    void load(range.start_date, range.end_date);
  }, [load, range]);

  const trendLines = [
    { key: "human", color: "#111", label: t("messages_tab.col_human") },
    { key: "agent", color: "#16a34a", label: t("messages_tab.col_agent") },
    { key: "system", color: "#6366f1", label: t("messages_tab.col_system") },
  ] as const;

  const barData = useMemo(() => {
    if (!data) return [];
    return data.type_distribution.map((row) => ({
      name: row.content_type,
      value: row.count,
    }));
  }, [data]);

  return (
    <div>
      <DatePresets value={range} onChange={setRange} />
      {error && <p style={shared.errorBanner}>{error}</p>}
      {loading && !data && (
        <p style={shared.loadingText}>{t("common.loading")}</p>
      )}
      {data && <KPICards totals={data.totals} />}
      {data && (
        <section style={shared.section}>
          <h3 style={shared.sectionTitle}>{t("messages_tab.daily_trends")}</h3>
          <TrendLineChart
            data={data.daily_trends}
            lines={trendLines}
            xKey="date"
          />
        </section>
      )}
      {barData.length > 0 && (
        <section style={shared.section}>
          <h3 style={shared.sectionTitle}>
            {t("messages_tab.type_distribution")}
          </h3>
          <BarDistribution data={barData} color="#6366f1" />
        </section>
      )}
      <section style={shared.section}>
        <button
          type="button"
          onClick={() => setShowTable(!showTable)}
          style={s.toggleBtn}
        >
          {showTable ? t("common.hide_details") : t("common.show_details")}
        </button>
        {showTable && data && (
          <>
            <DailyTrends rows={data.daily_trends} />
            <TypeDistribution rows={data.type_distribution} />
          </>
        )}
      </section>
    </div>
  );
}

// --- KPI Cards ---

function KPICards(props: {
  totals: MessageAnalytics["totals"];
}): React.JSX.Element {
  const { totals } = props;
  return (
    <section style={shared.section}>
      <div style={shared.cardGrid}>
        <Card
          label={t("messages_tab.total_messages")}
          value={fmt(totals.messages)}
        />
        <Card
          label={t("messages_tab.human_ratio")}
          value={pct(totals.human_ratio)}
        />
        <Card
          label={t("messages_tab.agent_ratio")}
          value={pct(totals.agent_ratio)}
        />
        <Card
          label={t("messages_tab.recall_rate")}
          value={pct(totals.recall_rate)}
        />
      </div>
    </section>
  );
}

function Card(props: {
  label: string;
  value: string;
}): React.JSX.Element {
  return (
    <div style={{ ...shared.card, padding: 20 }}>
      <span style={s.cardLabel}>{props.label}</span>
      <span style={shared.bigNumber}>{props.value}</span>
    </div>
  );
}

// --- Daily Trends Table ---

function DailyTrends(props: {
  rows: MessageAnalytics["daily_trends"];
}): React.JSX.Element {
  return (
    <div style={{ marginTop: 12 }}>
      <h3 style={shared.sectionTitle}>{t("messages_tab.daily_trends")}</h3>
      <table style={tableStyles.table}>
        <thead>
          <tr>
            <Th>{t("messages_tab.col_date")}</Th>
            <Th>{t("messages_tab.col_human")}</Th>
            <Th>{t("messages_tab.col_agent")}</Th>
            <Th>{t("messages_tab.col_system")}</Th>
            <Th>{t("messages_tab.col_total")}</Th>
          </tr>
        </thead>
        <tbody>
          {props.rows.map((row) => (
            <tr key={row.date}>
              <Td>{row.date}</Td>
              <Td mono>{fmt(row.human)}</Td>
              <Td mono>{fmt(row.agent)}</Td>
              <Td mono>{fmt(row.system)}</Td>
              <Td mono>{fmt(row.human + row.agent + row.system)}</Td>
            </tr>
          ))}
          {props.rows.length === 0 && (
            <tr>
              <Td colSpan={5}>{t("common.no_data_for_period")}</Td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// --- Type Distribution Table ---

function TypeDistribution(props: {
  rows: MessageAnalytics["type_distribution"];
}): React.JSX.Element {
  if (props.rows.length === 0) return <></>;
  return (
    <div style={{ marginTop: 16 }}>
      <h3 style={shared.sectionTitle}>{t("messages_tab.type_distribution")}</h3>
      <table style={tableStyles.table}>
        <thead>
          <tr>
            <Th>{t("messages_tab.col_content_type")}</Th>
            <Th>{t("messages_tab.col_count")}</Th>
            <Th>{t("messages_tab.col_percentage")}</Th>
          </tr>
        </thead>
        <tbody>
          {props.rows.map((row) => (
            <tr key={row.content_type}>
              <Td>{row.content_type}</Td>
              <Td mono>{fmt(row.count)}</Td>
              <Td mono>{pct(row.percentage / 100)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --- Hooks ---

function useMessageData(): {
  data: MessageAnalytics | null;
  error: string | null;
  loading: boolean;
  load: (start: string, end: string) => Promise<void>;
} {
  const [data, setData] = useState<MessageAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (start: string, end: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchMessageAnalytics({
          start_date: start,
          end_date: end,
        });
        setData(result);
      } catch (err: unknown) {
        setError(
          err instanceof Error ? err.message : t("common.failed_to_load"),
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, error, loading, load };
}

// --- Styles ---

const s: Record<string, React.CSSProperties> = {
  cardLabel: {
    display: "block",
    fontSize: 13,
    color: "var(--color-text-secondary)",
    fontWeight: 500,
    marginBottom: 4,
  },
  toggleBtn: {
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
    color: "var(--color-text)",
    cursor: "pointer",
  },
};
